import React, { useState } from "react";
import { Form, Input, Upload, message, Spin, Row, Col, Select, Button, Drawer } from "antd";
import { UploadOutlined, PlusOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import App from "../../Utils/LayoutMobile";
import { createLocation } from "../../features/locationSlice";

// ── Theme Constants ──
const C_PINK      = "#F04DA0";
const C_PURPLE    = "#8B2FC9";
const C_GRAD      = `linear-gradient(135deg, ${C_PINK}, ${C_PURPLE})`;
const C_SOFT_BG   = "#F8F4FF";
const C_TEXT_MAIN = "#1a0040";
const C_TEXT_MUTED= "#64748B";

// ── Icons ──
const SaveIcon = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>;
const CancelIcon = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const PinIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>;
const ExtIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>;

// ── Transport & Amenity SVGs (For Preview) ──
const TrainIcon  = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="3" width="16" height="14" rx="2"/><path d="M4 11h16M12 3v8M8 19l-2 3M18 22l-2-3M8 19h8"/></svg>;
const PlaneIcon  = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.8 19.2L16 11l3.5-3.5C21 6 21 4 19.5 2.5S18 2 16.5 3.5L13 7 4.8 5.2C4.4 5.1 4 5.4 4 5.8v.4c0 .3.1.5.4.6L8 9 6 11H4l-1 1 3 2 2 3 1-1v-2l2-2 2.6 3.6c.1.3.4.4.6.4h.4c.4 0 .7-.4.6-.8z"/></svg>;
const CarIcon    = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.5 2.9C2.1 10.9 2 11 2 11.1V16c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/></svg>;

const AmIcons = {
  "Pantry & Catering": <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>,
  "Power Backup": <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18.36 6.64a9 9 0 1 1-12.73 0"/><line x1="12" y1="2" x2="12" y2="12"/></svg>,
  "Foyer Lounge": <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v3"/><path d="M2 11a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-4z"/><path d="M4 17v2M20 17v2"/></svg>,
  "High-Speed WiFi": <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><circle cx="12" cy="20" r="1" fill="currentColor"/></svg>,
  "Registration Desk": <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="3" rx="1"/><path d="M6 10v7M18 10v7M4 17h16"/></svg>,
  "Central AC": <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="8" rx="2"/><path d="M7 14v4M12 14v4M17 14v4M7 18h10"/></svg>,
  "Security & CCTV": <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>,
  "Live Demo Stage": <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="14" width="20" height="6" rx="1"/><path d="M6 14V8a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v6"/><path d="M10 4h4"/></svg>
};

const AMENITY_OPTIONS = Object.keys(AmIcons).map(key => ({ value: key, label: key }));

const TRANSPORT_TYPES = [
  { value: "train", label: "Train Station" },
  { value: "plane", label: "Airport" },
  { value: "car", label: "Bus/Drive" },
  { value: "pin", label: "Landmark" },
];

function SectionLabel({ children }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
      <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.16em", textTransform:"uppercase", color:C_PINK, whiteSpace:"nowrap" }}>
        {children}
      </span>
      <span style={{ flex:1, height:1.5, background:`linear-gradient(90deg, rgba(240,77,160,0.35), transparent)`, borderRadius:2 }}/>
    </div>
  );
}

const LocationForm = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  
  // Preview State
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [photoIdx, setPhotoIdx] = useState(0);

  const handlePreview = () => {
    // Get live data from the form even if it hasn't been submitted
    const currentValues = form.getFieldsValue(true);
    setPreviewData(currentValues);
    setPhotoIdx(0);
    setPreviewVisible(true);
  };

  const handleSubmit = (values) => {
    setSubmitting(true);
    const formData = new FormData();
    
    formData.append("title", values.title);
    formData.append("city", values.city || "");
    formData.append("address", values.address || "");
    formData.append("link", values.link || "");
    if (values.description) formData.append("description", values.description);

    if (values.amenities) formData.append("amenities", JSON.stringify(values.amenities));
    if (values.highlights) formData.append("highlights", JSON.stringify(values.highlights));
    if (values.distances) formData.append("distances", JSON.stringify(values.distances));

    if (values.venue?.capacity?.area) formData.append("venue.capacity.area", values.venue.capacity.area);
    if (values.venue?.capacity?.carParking) formData.append("venue.capacity.carParking", values.venue.capacity.carParking);

    values.imageList?.forEach((file) => {
      if (file.originFileObj) formData.append("imageList", file.originFileObj);
    });

    dispatch(createLocation(formData)).unwrap()
      .then(() => {
        message.success("Location created successfully");
        form.resetFields();
        navigate("/location");
      })
      .catch((error) => message.error(error?.message || "Failed to create location"))
      .finally(() => setSubmitting(false));
  };

  // Helper to get preview images (creates object URLs for live uploaded files)
  const getPreviewImages = () => {
    if (!previewData?.imageList || previewData.imageList.length === 0) {
      return ["https://via.placeholder.com/800x500/f8f4ff/8b2fc9?text=Upload+Images+to+Preview"];
    }
    return previewData.imageList.map(file => {
      if (file.originFileObj) return URL.createObjectURL(file.originFileObj);
      return file.url;
    });
  };

  const previewImages = getPreviewImages();

  return (
    <App>
      <div className="lc">
        <div className="mw">
          
          <div className="header-flex">
            <div>
              <SectionLabel>Venue Setup</SectionLabel>
              <h1 className="sec-title">Add <span style={{ color: C_PINK }}>Location</span></h1>
              <p className="sec-sub">Fill in the complete details to publish this venue on the website.</p>
            </div>
            <div className="icon-wrap"><PinIcon /></div>
          </div>

          <Spin spinning={submitting}>
            <div className="glass form-wrapper">
              <Form 
                form={form} layout="vertical" onFinish={handleSubmit} className="premium-form" requiredMark={false}
                initialValues={{ highlights: [{}], distances: [{}] }}
              >
                {/* 1. Basic Details */}
                <h3 className="form-section-title">Basic Details</h3>
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item name="title" label="Venue Title" rules={[{ required: true }]}>
                      <Input placeholder="e.g. Buntara Bhavana Pune" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="city" label="City / Area" rules={[{ required: true }]}>
                      <Input placeholder="e.g. Baner, Pune" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item name="address" label="Full Address">
                      <Input placeholder="e.g. S No.104/1 Part..." />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="link" label="Google Maps Embed URL" rules={[{ required: true }]}>
                      <Input placeholder="Embed URL for Map Iframe" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col xs={24}>
                    <Form.Item name="description" label="About the Venue">
                      <Input.TextArea rows={4} placeholder="Describe the venue facilities..." />
                    </Form.Item>
                  </Col>
                </Row>

                <div className="form-divider" />

                {/* 2. Features & Stats */}
                <h3 className="form-section-title">Features & Capacity</h3>
                <Row gutter={24}>
                  <Col xs={24} md={6}>
                    <Form.Item name={["venue", "capacity", "area"]} label="Delegate Capacity">
                      <Input placeholder="e.g. 800+" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item name={["venue", "capacity", "carParking"]} label="Parking Capacity">
                      <Input placeholder="e.g. 200+ Cars" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="amenities" label="Amenities">
                      <Select mode="multiple" placeholder="Select available amenities" options={AMENITY_OPTIONS} className="custom-select" />
                    </Form.Item>
                  </Col>
                </Row>

                <div className="form-divider" />

                {/* 3. Event Highlights */}
                <h3 className="form-section-title">Event Highlights (Stats)</h3>
                <Form.List name="highlights">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Row gutter={16} key={key} align="middle" style={{ marginBottom: 12 }}>
                          <Col span={10}>
                            <Form.Item {...restField} name={[name, 'value']} noStyle><Input placeholder="Stat (e.g., 50+)" /></Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item {...restField} name={[name, 'label']} noStyle><Input placeholder="Label (e.g., Expert Speakers)" /></Form.Item>
                          </Col>
                          <Col span={2}><Button danger type="text" icon={<DeleteOutlined />} onClick={() => remove(name)} /></Col>
                        </Row>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} className="add-btn">Add Highlight</Button>
                    </>
                  )}
                </Form.List>

                <div className="form-divider" />

                {/* 4. Quick Access / Distances */}
                <h3 className="form-section-title">Quick Access (Distances)</h3>
                <Form.List name="distances">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Row gutter={16} key={key} align="middle" style={{ marginBottom: 12 }}>
                          <Col span={6}>
                            <Form.Item {...restField} name={[name, 'type']} noStyle>
                              <Select placeholder="Transport" options={TRANSPORT_TYPES} style={{ width: '100%' }} className="custom-select" />
                            </Form.Item>
                          </Col>
                          <Col span={10}>
                            <Form.Item {...restField} name={[name, 'label']} noStyle><Input placeholder="Location (e.g., Airport)" /></Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item {...restField} name={[name, 'distance']} noStyle><Input placeholder="Distance (e.g., 18 km)" /></Form.Item>
                          </Col>
                          <Col span={2}><Button danger type="text" icon={<DeleteOutlined />} onClick={() => remove(name)} /></Col>
                        </Row>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} className="add-btn">Add Distance Entry</Button>
                    </>
                  )}
                </Form.List>

                <div className="form-divider" />

                {/* 5. Media */}
                <h3 className="form-section-title">Media</h3>
                <Form.Item name="imageList" valuePropName="fileList" getValueFromEvent={(e) => e?.fileList}>
                  <Upload beforeUpload={() => false} listType="picture" multiple className="custom-upload">
                    <div className="upload-btn-inner">
                      <UploadOutlined style={{ fontSize: '18px', color: C_PINK }} />
                      <span>Select Venue Images (Gallery)</span>
                    </div>
                  </Upload>
                </Form.Item>

                {/* Actions */}
                <div className="form-actions">
                  <button type="button" className="btn-cancel" onClick={() => navigate("/location")} disabled={submitting}>
                    <CancelIcon /> Cancel
                  </button>
                  <button type="submit" className="btn-cta" disabled={submitting}>
                    <SaveIcon /> Create Complete Location
                  </button>
                </div>
              </Form>
            </div>
          </Spin>

        </div>
      </div>

      {/* ── FLOATING LIVE PREVIEW BUTTON ── */}
      <button className="floating-preview-btn" onClick={handlePreview} title="Live Preview">
        <EyeOutlined style={{ fontSize: '20px' }} />
        <span>Live Preview</span>
      </button>

      {/* ── LIVE PREVIEW DRAWER ── */}
      <Drawer
        title={<span style={{ fontWeight: 800, color: C_TEXT_MAIN }}>Website Live Preview</span>}
        placement="right"
        width="100%"
        onClose={() => setPreviewVisible(false)}
        open={previewVisible}
        bodyStyle={{ padding: 0, background: C_SOFT_BG }}
      >
        {previewData && (
          <div className="website-preview-root">
            <div className="wp-lc">
              <div className="wp-mw">
                
                {/* Hero */}
                <div className="wp-hero">
                  <div className="wp-gallery-col">
                    <SectionLabel>Premium Venue</SectionLabel>
                    <h1 className="wp-main-title">{previewData.title || "Venue Title Here"}</h1>
                    <p className="wp-desc">{previewData.description || "Add a description to see it appear here on the main website."}</p>
                    
                    <div className="wp-main-img">
                      <img src={previewImages[photoIdx]} alt="Preview Main" />
                    </div>
                    
                    {previewImages.length > 1 && (
                      <div className="wp-thumb-row">
                        {previewImages.map((src, i) => (
                          <div key={i} className={`wp-thumb-item ${i === photoIdx ? "on" : ""}`} onClick={() => setPhotoIdx(i)}>
                            <img src={src} alt={`Thumb ${i}`} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="wp-info-col">
                    {/* Capacity Box */}
                    <div className="wp-glass">
                      <SectionLabel>Venue Capacity</SectionLabel>
                      <div className="wp-stat-grid">
                        <div className="wp-stat-box">
                          <span className="wp-stat-val">{previewData.venue?.capacity?.area || "0"}</span>
                          <span className="wp-stat-lbl">Delegates</span>
                        </div>
                        <div className="wp-stat-box">
                          <span className="wp-stat-val">{previewData.venue?.capacity?.carParking || "0"}</span>
                          <span className="wp-stat-lbl">Parking</span>
                        </div>
                      </div>
                    </div>

                    {/* Distances */}
                    <div className="wp-glass">
                      <SectionLabel>Quick Access</SectionLabel>
                      {(previewData.distances || []).map((d, i) => d.label && (
                        <div key={i} className="wp-dist-row">
                          <span className="wp-dist-lbl">
                            <span style={{ color: C_PINK }}>
                              {d.type === 'train' ? <TrainIcon/> : d.type === 'plane' ? <PlaneIcon/> : d.type === 'car' ? <CarIcon/> : <PinIcon/>}
                            </span>
                            {d.label}
                          </span>
                          <span className="wp-dist-val">{d.distance}</span>
                        </div>
                      ))}
                      {(!previewData.distances || previewData.distances.length === 0 || !previewData.distances[0].label) && (
                        <p style={{ fontSize: 13, color: C_TEXT_MUTED }}>Add distances to see them here.</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* About Section */}
                <div className="wp-about-section">
                  <div className="wp-about-title-block">
                    <SectionLabel>About the Location</SectionLabel>
                    <h2 className="wp-sec-title">Where Expertise Meets Excellence</h2>
                    <div style={{ marginTop: 18 }}>
                      <div className="wp-about-badge">
                        <span style={{ color: C_PINK }}><PinIcon/></span>
                        {previewData.city || "City / Area Here"}
                      </div>
                    </div>
                  </div>

                  <div className="wp-about-img-wrap">
                     <img src={previewImages[0]} alt="Side Preview" />
                     <div className="wp-about-img-tag"><span style={{ color:C_PINK }}><PinIcon/></span>{previewData.city || "City"}</div>
                  </div>

                  <div className="wp-about-text-block">
                     <p className="wp-about-body">{previewData.description || "Description preview..."}</p>
                     <div className="wp-about-highlights">
                        {(previewData.highlights || []).map((h, i) => h.value && (
                          <div className="wp-ab-hi" key={i}>
                            <span className="wp-ab-hi-val">{h.value}</span>
                            <span className="wp-ab-hi-lbl">{h.label}</span>
                          </div>
                        ))}
                     </div>
                  </div>
                </div>

                {/* Amenities */}
                {previewData.amenities && previewData.amenities.length > 0 && (
                  <div className="wp-am-section">
                    <SectionLabel>Conference Experience</SectionLabel>
                    <h2 className="wp-sec-title">World-Class Amenities</h2>
                    <div className="wp-am-grid">
                      {previewData.amenities.map((am, i) => (
                        <div className="wp-am-card" key={i}>
                          <div className="wp-am-badge">{AmIcons[am] || <PinIcon/>}</div>
                          <div>
                            <div className="wp-am-name">{am}</div>
                            <div className="wp-am-desc">Included in venue</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Map */}
                {previewData.link && (
                  <div className="wp-map-section">
                    <div className="wp-map-frame">
                      <iframe title="Map Preview" src={previewData.link} allowFullScreen loading="lazy" />
                    </div>
                    <div className="wp-map-detail">
                      <div className="wp-map-addr-label"><span style={{ color:C_PINK }}><PinIcon/></span>conference venue</div>
                      <div className="wp-map-city">{previewData.title}</div>
                      <p className="wp-map-addr-text">{previewData.address}</p>
                      <button className="wp-btn-cta"><ExtIcon/> Open in Maps</button>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        )}
      </Drawer>

      {/* ── Styles ── */}
      <style>{`
        * { box-sizing: border-box; }
        .lc { font-family: 'Segoe UI', sans-serif; background: ${C_SOFT_BG}; min-height: 100vh; padding: 40px 0 80px; }
        .mw { max-width: 900px; margin: 0 auto; padding: 0 24px; }

        /* Admin Layout */
        .header-flex { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 32px; }
        .sec-title { font-size: clamp(28px, 4vw, 38px); font-weight: 800; color: ${C_TEXT_MAIN}; letter-spacing: -0.02em; line-height: 1.15; margin: 0 0 8px; }
        .sec-sub { font-size: 15px; color: ${C_TEXT_MUTED}; margin: 0; }
        .icon-wrap { width: 54px; height: 54px; background: rgba(240,77,160,0.1); border: 1px solid rgba(240,77,160,0.2); border-radius: 16px; display: flex; align-items: center; justify-content: center; color: ${C_PINK}; }

        .glass { background: #fff; border-radius: 20px; padding: 36px 40px; border: 1px solid rgba(139,47,201,0.08); box-shadow: 0 10px 40px rgba(0,0,0,0.04); }
        .form-section-title { font-size: 17px; font-weight: 800; color: ${C_PURPLE}; margin-top: 0; margin-bottom: 20px; }
        .form-divider { height: 1px; background: rgba(139,47,201,0.08); margin: 32px 0; }

        .premium-form .ant-form-item-label > label { font-weight: 600 !important; color: ${C_TEXT_MAIN} !important; font-size: 13.5px !important; }
        .premium-form .ant-input, .custom-select .ant-select-selector { padding: 10px 16px !important; border-radius: 10px !important; border: 1px solid rgba(139,47,201,0.15) !important; background: #faf6fe !important; color: ${C_TEXT_MAIN} !important; font-size: 14.5px !important; transition: all 0.3s; min-height: 44px; }
        .premium-form .ant-input:focus, .premium-form .ant-input:hover, .custom-select:hover .ant-select-selector { border-color: ${C_PINK} !important; background: #fff !important; box-shadow: 0 0 0 3px rgba(240,77,160,0.1) !important; }

        .add-btn { height: 44px; border-radius: 10px; border-color: rgba(139,47,201,0.3); color: ${C_PURPLE}; font-weight: 600; background: rgba(139,47,201,0.03); margin-top: 8px; }
        .custom-upload .ant-upload-select { display: block !important; width: 100%; }
        .upload-btn-inner { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 16px; background: #fff0f8; border: 1.5px dashed ${C_PINK}; border-radius: 12px; color: ${C_TEXT_MAIN}; font-weight: 600; font-size: 14.5px; cursor: pointer; transition: all 0.2s; }

        .form-actions { display: flex; align-items: center; justify-content: flex-end; gap: 16px; margin-top: 40px; padding-top: 24px; border-top: 1px solid rgba(139,47,201,0.08); }
        
        .btn-cta { display: inline-flex; align-items: center; gap: 8px; padding: 14px 28px; background: ${C_GRAD}; border: none; border-radius: 50px; color: #fff; font-size: 14.5px; font-weight: 700; cursor: pointer; box-shadow: 0 10px 24px rgba(240,77,160,0.3); transition: all 0.25s; }
        .btn-cta:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 14px 28px rgba(240,77,160,0.4); }
        .btn-cta:disabled { opacity: 0.7; cursor: not-allowed; }
        .btn-cancel { display: inline-flex; align-items: center; gap: 8px; padding: 14px 24px; background: transparent; border: 1px solid rgba(139,47,201,0.2); border-radius: 50px; color: ${C_TEXT_MUTED}; font-size: 14.5px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
        .btn-cancel:hover:not(:disabled) { background: #faf6fe; color: ${C_TEXT_MAIN}; border-color: rgba(139,47,201,0.4); }
        
        /* ── FLOATING BUTTON ── */
        .floating-preview-btn {
          position: fixed; bottom: 30px; right: 30px; z-index: 1000;
          background: linear-gradient(135deg, #c026d3, #6b19a3); color: white;
          border: none; border-radius: 50px; padding: 14px 24px;
          box-shadow: 0 6px 20px rgba(192,38,211,0.4);
          cursor: pointer; display: flex; align-items: center; gap: 10px;
          font-weight: 700; font-size: 15px; transition: transform 0.2s, box-shadow 0.2s;
        }
        .floating-preview-btn:hover { transform: translateY(-3px); box-shadow: 0 10px 24px rgba(192,38,211,0.5); }

        @media(max-width: 768px) {
          .glass { padding: 24px 20px; border-radius: 16px; }
          .header-flex { flex-direction: column; align-items: flex-start; gap: 16px; }
          .icon-wrap { display: none; }
          .form-actions { flex-direction: column-reverse; align-items: stretch; }
          .btn-cta, .btn-cancel { width: 100%; justify-content: center; margin-bottom: 8px;}
          .floating-preview-btn { bottom: 20px; right: 20px; padding: 12px 20px; }
        }

        /* ── SCOPED WEBSITE PREVIEW CSS ── */
        .website-preview-root .wp-lc { padding: 40px 0; font-family: 'Segoe UI', sans-serif; color: ${C_TEXT_MAIN}; }
        .website-preview-root .wp-mw { max-width: 1100px; margin: 0 auto; width: 90vw; }
        .website-preview-root .wp-main-title { font-size: clamp(32px,5vw,52px); font-weight: 800; letter-spacing: -0.02em; line-height: 1.1; margin-bottom: 14px; }
        .website-preview-root .wp-desc { font-size: 15px; color: ${C_TEXT_MUTED}; line-height: 1.75; margin-bottom: 22px; }
        .website-preview-root .wp-hero { display: grid; grid-template-columns: 1.3fr 0.9fr; gap: 52px; margin-bottom: 80px; align-items: start; }
        .website-preview-root .wp-gallery-col { display: flex; flex-direction: column; gap: 14px; }
        .website-preview-root .wp-main-img { border-radius: 20px; overflow: hidden; aspect-ratio: 16/10; box-shadow: 0 20px 48px rgba(139,47,201,0.14); }
        .website-preview-root .wp-main-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .website-preview-root .wp-thumb-row { display: flex; gap: 10px; overflow-x: auto; padding-bottom: 4px; }
        .website-preview-root .wp-thumb-item { flex: 0 0 calc((100% - 50px)/6); min-width: 80px; height: 62px; border-radius: 11px; overflow: hidden; cursor: pointer; border: 2.5px solid transparent; opacity: 0.6; }
        .website-preview-root .wp-thumb-item.on { border-color: ${C_PINK}; opacity: 1; }
        .website-preview-root .wp-thumb-item img { width: 100%; height: 100%; object-fit: cover; }
        .website-preview-root .wp-info-col { display: flex; flex-direction: column; gap: 20px; position: sticky; top: 24px; }
        .website-preview-root .wp-glass { background: #fff; border-radius: 20px; padding: 28px; border: 1px solid rgba(139,47,201,0.08); box-shadow: 0 8px 28px rgba(0,0,0,0.04); }
        .website-preview-root .wp-stat-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 20px; }
        .website-preview-root .wp-stat-box { background: ${C_SOFT_BG}; border-radius: 14px; padding: 18px; text-align: center; }
        .website-preview-root .wp-stat-val { display: block; font-size: 22px; font-weight: 800; color: ${C_PURPLE}; margin-bottom: 4px; }
        .website-preview-root .wp-stat-lbl { font-size: 11px; font-weight: 600; text-transform: uppercase; color: ${C_TEXT_MUTED}; }
        .website-preview-root .wp-dist-row { display: flex; justify-content: space-between; align-items: center; padding: 14px 0; border-bottom: 1px solid rgba(139,47,201,0.07); }
        .website-preview-root .wp-dist-lbl { display: flex; align-items: center; gap: 10px; font-weight: 500; }
        .website-preview-root .wp-dist-val { font-weight: 700; color: ${C_PURPLE}; }
        
        .website-preview-root .wp-about-section { display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: auto auto; column-gap: 52px; margin-bottom: 88px; }
        .website-preview-root .wp-about-title-block { grid-column: 1; grid-row: 1; align-self: end; }
        .website-preview-root .wp-sec-title { font-size: clamp(24px,3.5vw,36px); font-weight: 800; line-height: 1.15; margin-bottom: 8px; }
        .website-preview-root .wp-about-badge { display: inline-flex; align-items: center; gap: 8px; background: linear-gradient(135deg,rgba(240,77,160,0.08),rgba(139,47,201,0.1)); border: 1px solid rgba(139,47,201,0.15); border-radius: 50px; padding: 8px 18px; font-size: 13px; font-weight: 600; color: ${C_PURPLE}; margin-bottom: 22px; }
        .website-preview-root .wp-about-img-wrap { grid-column: 2; grid-row: 1 / 3; border-radius: 24px; overflow: hidden; position: relative; }
        .website-preview-root .wp-about-img-wrap img { width: 100%; height: 380px; object-fit: cover; }
        .website-preview-root .wp-about-img-tag { position: absolute; bottom: 20px; left: 20px; background: rgba(255,255,255,0.92); backdrop-filter: blur(8px); border-radius: 50px; padding: 8px 18px; font-size: 13px; font-weight: 700; display: flex; align-items: center; gap: 7px; }
        .website-preview-root .wp-about-text-block { grid-column: 1; grid-row: 2; }
        .website-preview-root .wp-about-body { font-size: 15px; color: ${C_TEXT_MUTED}; line-height: 1.8; margin-bottom: 18px; }
        .website-preview-root .wp-about-highlights { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 24px; }
        .website-preview-root .wp-ab-hi { background: #fff; border: 1px solid rgba(139,47,201,0.09); border-radius: 14px; padding: 16px 18px; display: flex; flex-direction: column; gap: 4px; }
        .website-preview-root .wp-ab-hi-val { font-size: 20px; font-weight: 800; color: ${C_PURPLE}; }
        .website-preview-root .wp-ab-hi-lbl { font-size: 12px; color: ${C_TEXT_MUTED}; font-weight: 500; }

        .website-preview-root .wp-am-section { margin-bottom: 88px; }
        .website-preview-root .wp-am-grid { display: grid; grid-template-columns: repeat(auto-fill,minmax(250px,1fr)); gap: 18px; margin-top: 28px; }
        .website-preview-root .wp-am-card { display: flex; align-items: flex-start; gap: 16px; padding: 22px; background: #fff; border-radius: 18px; border: 1px solid rgba(139,47,201,0.07); }
        .website-preview-root .wp-am-badge { width: 46px; height: 46px; border-radius: 12px; background: ${C_GRAD}; color: #fff; display: flex; align-items: center; justify-content: center; }
        .website-preview-root .wp-am-name { font-size: 15px; font-weight: 700; margin-bottom: 4px; }
        .website-preview-root .wp-am-desc { font-size: 13px; color: ${C_TEXT_MUTED}; }

        .website-preview-root .wp-map-section { display: grid; grid-template-columns: 1.5fr 1fr; background: #fff; border-radius: 28px; overflow: hidden; border: 1px solid rgba(139,47,201,0.08); }
        .website-preview-root .wp-map-frame { width: 100%; min-height: 420px; }
        .website-preview-root .wp-map-frame iframe { width: 100%; height: 100%; border: none; }
        .website-preview-root .wp-map-detail { padding: 48px 40px; display: flex; flex-direction: column; justify-content: center; }
        .website-preview-root .wp-map-addr-label { font-size: 11px; font-weight: 700; letter-spacing: 0.15em; text-transform: uppercase; color: ${C_PINK}; display: flex; align-items: center; gap: 6px; margin-bottom: 14px; }
        .website-preview-root .wp-map-city { font-size: 28px; font-weight: 800; margin-bottom: 12px; line-height: 1.2; }
        .website-preview-root .wp-map-addr-text { font-size: 15px; color: ${C_TEXT_MUTED}; line-height: 1.8; margin-bottom: 28px; }
        .website-preview-root .wp-btn-cta { display: inline-flex; align-items: center; justify-content: center; gap: 9px; padding: 16px 28px; background: ${C_GRAD}; border: none; border-radius: 50px; color: #fff; font-weight: 700; cursor: pointer; }

        @media(max-width: 1024px) {
          .website-preview-root .wp-hero { grid-template-columns: 1fr; gap: 24px; }
          .website-preview-root .wp-info-col { position: static; display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
          .website-preview-root .wp-about-section { display: flex; flex-direction: column; gap: 24px; }
          .website-preview-root .wp-about-title-block { order: 1; align-self: flex-start; }
          .website-preview-root .wp-about-img-wrap { order: 2; height: auto; width: 100%; }
          .website-preview-root .wp-about-text-block { order: 3; align-self: flex-start; }
          .website-preview-root .wp-map-section { grid-template-columns: 1fr; }
        }
      `}</style>
    </App>
  );
};

export default LocationForm;